import { Controller } from '@hotwired/stimulus'

// Чат-ассистент по паделу.
// - Открытие/закрытие панели, esc
// - Диалог привязан к токену в localStorage, история восстанавливается с сервера
// - Отправка: POST /assistant/messages, ответ приходит готовым безопасным HTML
export default class AssistantChatController extends Controller {
  static targets = [
    'panel',
    'launcher',
    'messages',
    'typing',
    'form',
    'input',
    'send'
  ]
  static values = { url: String }

  static STORAGE_KEY = 'assistant_conversation_token'

  connect() {
    this.isOpen = false
    this.isSending = false
    this.historyLoaded = false
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.isOpen = true
    this.element.classList.add('is-open')
    this.inputTarget.focus()

    if (!this.historyLoaded) {
      this.historyLoaded = true
      this.restoreHistory()
    }
  }

  close() {
    this.isOpen = false
    this.element.classList.remove('is-open')
  }

  submitOnEnter(event) {
    if (event.shiftKey) return // shift+enter — перенос строки

    event.preventDefault()
    this.formTarget.requestSubmit()
  }

  autosize() {
    const input = this.inputTarget
    input.style.height = 'auto'
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`
  }

  async send(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    if (!text || this.isSending) return

    this.appendBubble('user', null, text)
    this.inputTarget.value = ''
    this.autosize()
    this.setSending(true)

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify({
          message: text,
          // eslint-disable-next-line camelcase -- имена параметров Rails-контроллера
          conversation_token: this.token,
          // eslint-disable-next-line camelcase -- имена параметров Rails-контроллера
          page_path: window.location.pathname
        })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.appendBubble(
          'error',
          null,
          data.message || 'Something went wrong. Please try again.'
        )
        return
      }

      this.token = data.conversation_token
      this.appendBubble('assistant', data.message.html)
    } catch {
      this.appendBubble('error', null, 'Network error. Please try again.')
    } finally {
      this.setSending(false)
      this.inputTarget.focus()
    }
  }

  async restoreHistory() {
    if (!this.token) return

    try {
      const url = `${this.urlValue}?conversation_token=${encodeURIComponent(this.token)}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) return

      const data = await response.json()
      for (const message of data.messages || []) {
        this.appendBubble(message.role, message.html)
      }
    } catch {
      // История не критична — молча оставляем приветствие
    }
  }

  // --- helpers ---

  get token() {
    try {
      return localStorage.getItem(AssistantChatController.STORAGE_KEY)
    } catch {
      return this._token || null
    }
  }

  set token(value) {
    if (!value) return
    this._token = value
    try {
      localStorage.setItem(AssistantChatController.STORAGE_KEY, value)
    } catch {
      // приватный режим — токен проживёт до перезагрузки
    }
  }

  setSending(sending) {
    this.isSending = sending
    this.sendTarget.disabled = sending
    this.typingTarget.classList.toggle('hidden', !sending)
    if (sending) this.scrollToBottom()
  }

  appendBubble(role, html, text = null) {
    const bubble = document.createElement('div')
    bubble.className = `assistant__bubble assistant__bubble--${role === 'error' ? 'error' : role}`
    if (html !== null) {
      bubble.innerHTML = html // безопасный HTML, отрендеренный сервером
    } else {
      const p = document.createElement('p')
      p.textContent = text
      bubble.appendChild(p)
    }
    this.messagesTarget.appendChild(bubble)
    this.scrollToBottom()
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {})
    }
  }
}
